import { useState } from 'react'
import {
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

// Tab-specific "who shows up here?" help. The criteria mirror exactly what the listing does:
//   - specialty / no-specialty tabs hit /screeners/clinical/list (service-approved + KYC-approved
//     + clinic-associated screeners) then split on targetSpecialitySlugs.
//   - the red "All Screeners (by role)" tab hits /screeners/role-holders/list — the union of both
//     ROLE_SCREENER sources with no KYC / enabled / clinic gating, so gaps surface.
function getCriteriaConfig(kind, label) {
  if (kind === 'role-holders') {
    return {
      accent: '#dc2626',
      title: 'Who shows in “All Screeners (by role)”',
      intro:
        'Everyone who is a screener by either role source — shown even if not fully set up, so gaps stand out. A practitioner appears when at least one of these is true:',
      bullets: [
        'Has ROLE_SCREENER in their User.java authorities (method 1), and/or',
        'Has the servicesApproved.ROLE_SCREENER block on their practitioner record (method 2).',
      ],
      note:
        'Unlike the other tabs, this does NOT require KYC approval, an enabled service block, or a clinic association. Each row highlights which source is missing, whether the service block is disabled, and whether a Screener Profile exists — and lets a ROLE_SCREENER_ADMIN create the Screener Profile (once the service block is enabled).',
    }
  }
  if (kind === 'no-specialty') {
    return {
      accent: '#6366f1',
      title: 'Who shows in “No specialty”',
      intro:
        'Clinical screeners not yet tagged to a specialty. A screener appears only when ALL of these are true:',
      bullets: [
        'ROLE_SCREENER service approval is enabled (servicesApproved.ROLE_SCREENER.enabled = true).',
        'No target specialties set (servicesApproved.ROLE_SCREENER.targetSpecialitySlugs is empty).',
        'KYC status is “approved”.',
        'Not deleted.',
        'Associated with a clinic (listed in a clinic location’s screeners or a clinic brand’s workforce).',
      ],
      note: 'Paused screeners still appear here, greyed out.',
    }
  }
  return {
    accent: '#6366f1',
    title: `Who shows in “${label}”`,
    intro:
      'Clinical screeners tagged to this specialty. A screener appears only when ALL of these are true:',
    bullets: [
      'ROLE_SCREENER service approval is enabled (servicesApproved.ROLE_SCREENER.enabled = true).',
      `Target specialties include this one (servicesApproved.ROLE_SCREENER.targetSpecialitySlugs contains “${label}”).`,
      'KYC status is “approved”.',
      'Not deleted.',
      'Associated with a clinic (listed in a clinic location’s screeners or a clinic brand’s workforce).',
    ],
    note: 'Paused screeners still appear here, greyed out.',
  }
}

/**
 * Contextual help for the screeners directory tabs. Renders a single info button next to the tab
 * strip; the popover always describes the currently selected tab (passed via `info`), so the help
 * is effectively per-tab as the user switches between specialty / no-specialty / role tabs.
 */
export default function ScreenerTabCriteriaInfo({ info }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const config = getCriteriaConfig(info?.kind, info?.label)

  return (
    <>
      <Tooltip title="Which screeners show in this tab?" arrow>
        <IconButton
          size="small"
          aria-label="Which screeners show in this tab?"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ color: config.accent, flexShrink: 0 }}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            maxWidth: 400,
            p: 2,
            borderRadius: 2,
            borderTop: `3px solid ${config.accent}`,
            boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {config.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          {config.intro}
        </Typography>
        <Stack spacing={0.75}>
          {config.bullets.map((bullet) => (
            <Box key={bullet} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 16, mt: '2px', color: config.accent, flexShrink: 0 }}
              />
              <Typography variant="body2">{bullet}</Typography>
            </Box>
          ))}
        </Stack>
        {config.note && (
          <>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="caption" color="text.secondary">
              {config.note}
            </Typography>
          </>
        )}
      </Popover>
    </>
  )
}
